import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Shield, X, Trophy, Users, UserPlus, ScrollText, Coins, Gift, CheckCircle2, Clock, Skull, Radio, Activity, Zap, Satellite, TrendingUp, Star, Filter, Swords, ChevronRight, Percent, RefreshCw, Map, Compass, Landmark } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { modeColor } from "@/lib/gamemode";

const CAT_ICON = { skilling: Star, quests: ScrollText, diaries: Map, combat: Swords, raids: Landmark, collection: Trophy, clues: Compass };

const modeLabel = (m) => (m === "hardcore" ? "HCIM" : m === "ultimate" ? "UIM" : m === "ironman" ? "IM" : "Main");
const fmtWhen = (iso) => {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function AdminDashboard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [winners, setWinners] = useState([]);
  const [standings, setStandings] = useState([]);
  const [recent, setRecent] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [previews, setPreviews] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [accountsModal, setAccountsModal] = useState(null);
  const [referralsModal, setReferralsModal] = useState(null);
  const [acctFilter, setAcctFilter] = useState("");
  const [dupOnly, setDupOnly] = useState(false);
  const [freshOnly, setFreshOnly] = useState(false);
  const [expandedEmails, setExpandedEmails] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  const [fulfilling, setFulfilling] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [tick, setTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);

  const manualRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await load(true);
    setRefreshing(false);
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, w, lb, rc, bd, pv] = await Promise.all([
        api.get("/admin/summary"),
        api.get("/admin/winners"),
        api.get("/leaderboard?scope=season"),
        api.get("/admin/recent-signups"),
        api.get("/admin/breakdown"),
        api.get("/admin/demo-previews"),
      ]);
      setSummary(s.data);
      setWinners(w.data);
      setStandings((lb.data.entries || []).slice(0, 10));
      setRecent(rc.data || []);
      setBreakdown(bd.data || null);
      setPreviews(pv.data || null);
      setUpdatedAt(Date.now());
    } catch (e) {
      if (!silent) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setOpen(false); }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const openUser = async (uid) => {
    setUserDetail({ loading: true });
    try {
      const { data } = await api.get(`/admin/user/${uid}`);
      setUserDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setUserDetail(null);
    }
  };

  const banUser = async (userId, email, currentlyBanned) => {
    if (!currentlyBanned) {
      const reason = window.prompt(`Ban ${email}?\nThis blocks login for the whole account and removes them from leaderboards.\n\nOptional reason:`, "");
      if (reason === null) return;
      try {
        await api.post(`/admin/user/${userId}/ban`, { reason });
        toast.success(`Banned ${email}`);
        setAccountsModal((m) => m ? { ...m, accounts: m.accounts.map((a) => a.user_id === userId ? { ...a, banned: true } : a) } : m);
        setUserDetail((d) => d && d.user_id === userId ? { ...d, banned: true } : d);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    } else {
      if (!window.confirm(`Unban ${email}?`)) return;
      try {
        await api.post(`/admin/user/${userId}/unban`);
        toast.success(`Unbanned ${email}`);
        setAccountsModal((m) => m ? { ...m, accounts: m.accounts.map((a) => a.user_id === userId ? { ...a, banned: false } : a) } : m);
        setUserDetail((d) => d && d.user_id === userId ? { ...d, banned: false } : d);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }
  };

  const openModal = () => { setOpen(true); load(); };

  const openReferrals = async () => {
    setReferralsModal({ loading: true });
    try {
      const { data } = await api.get("/admin/referrals");
      setReferralsModal(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setReferralsModal(null);
    }
  };

  const resolveReferral = async (inviteeId, action) => {
    try {
      const { data } = await api.post(`/admin/referrals/${inviteeId}/${action}`);
      setReferralsModal((m) => m ? {
        ...m,
        referrers: m.referrers.map((g) => ({
          ...g,
          invitees: g.invitees.map((inv) => inv.user_id === inviteeId ? { ...inv, reward_status: data.reward_status } : inv),
        })),
      } : m);
      toast.success(action === "approve" ? "Referral reward granted (+1 skip each)." : "Referral reward voided.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openAccounts = async (verifiedOnly) => {
    setAcctFilter(""); setDupOnly(false); setFreshOnly(false); setExpandedEmails(new Set()); setPage(0);
    setAccountsModal({ loading: true, verifiedOnly, title: verifiedOnly ? "Verified accounts" : "All accounts" });
    try {
      const { data } = await api.get(`/admin/accounts${verifiedOnly ? "?verified_only=true" : ""}`);
      setAccountsModal({ ...data, verifiedOnly, title: verifiedOnly ? "Verified accounts" : "All accounts" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setAccountsModal(null);
    }
  };

  // auto-refresh every 15s while the modal is open + a 1s clock for "updated Xs ago"
  useEffect(() => {
    if (!open) return;
    pollRef.current = setInterval(() => load(true), 15000);
    const clock = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(pollRef.current); clearInterval(clock); };
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => { setPage(0); }, [acctFilter, dupOnly, freshOnly]);

  const agoText = updatedAt ? (() => {
    const s = Math.floor((Date.now() - updatedAt) / 1000);
    return s < 2 ? "just now" : `${s}s ago`;
  })() : "";

  const fulfill = async (w) => {
    setFulfilling(`${w.season}:${w.user_id}`);
    try {
      await api.post(`/admin/seasons/${w.season}/fulfill?user_id=${encodeURIComponent(w.user_id)}`);
      setWinners((prev) => prev.map((x) => (x.season === w.season && x.user_id === w.user_id ? { ...x, bond_status: "fulfilled" } : x)));
      setSummary((s) => (s ? { ...s, pending_claims: Math.max(0, s.pending_claims - 1) } : s));
      toast.success(`Bond marked fulfilled for ${w.name} (${w.season}).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setFulfilling(null);
    }
  };

  const stat = (icon, label, value, testid, onClick) => (
    <div
      className={`osrs-panel p-3 text-center ${onClick ? "cursor-pointer hover:brightness-125 transition-[filter]" : ""}`}
      data-testid={testid}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <div className="flex items-center justify-center mb-1">{icon}</div>
      <div className="osrs-num osrs-title text-2xl">{value}</div>
      <div className="osrs-shadow text-[10px] text-[#b9a06a] uppercase">{label}{onClick ? " →" : ""}</div>
    </div>
  );

  const renderAccountCard = (acc, dup) => {
    const fresh = acc.suspicious_fresh;
    return (
      <li key={acc.profile_id} className={`bg-black/30 border p-3 ${(dup || fresh) ? "border-[#FF9040]" : "border-[#0f0d09]"}`} data-testid={`verified-account-${acc.profile_id}`}>
        <div className="flex items-center justify-between gap-2 mb-1">
          <button
            onClick={() => { setAccountsModal(null); openUser(acc.user_id); }}
            className="osrs-num text-left hover:underline flex items-center gap-1.5 flex-wrap"
            style={{ color: modeColor(acc.gamemode) }}
            data-testid={`verified-account-link-${acc.profile_id}`}
          >
            {acc.rsn} <span className="text-[10px] uppercase">{modeLabel(acc.gamemode)}</span>
            {acc.verified && <CheckCircle2 size={12} className="text-[#7CFC7C]" title="Verified" />}
            {dup && <span className="text-[10px] text-[#FF9040]" title="Same RSN under multiple emails">⚠ DUPE</span>}
            {fresh && <span className="text-[10px] text-[#FF9040]" title="Low level but earning points">🌱 FRESH</span>}
          </button>
          <span className="osrs-shadow text-[11px] text-[#b9a06a] shrink-0">CB {acc.combat_level ?? "—"} · Total {(acc.total_level || 0).toLocaleString()} · {acc.streak}🔥</span>
        </div>
        <p className="osrs-shadow text-[10px] text-[#8a7a54] mb-1 truncate">created {fmtWhen(acc.created_at)} · synced {fmtWhen(acc.last_refresh_at)}</p>
        <p className="osrs-shadow text-[10px] mb-1.5" data-testid={`account-points-${acc.profile_id}`}>
          <Star size={10} className="inline mr-1 text-[#FFCF3F]" />
          <span className={fresh ? "text-[#FF9040]" : "text-[#7CFC7C]"}>{(acc.points_earned || 0).toLocaleString()} pts</span>
          <span className="text-[#8a7a54]"> from {acc.completed_count || 0} completed</span>
        </p>
        <p className="osrs-shadow text-[10px] mb-1.5" data-testid={`account-today-${acc.profile_id}`}>
          <span className="text-[#8a7a54] uppercase mr-1">Today:</span>
          <span className="text-[#7CFC7C]">{acc.completed_today || 0} done</span>
          <span className="text-[#8a7a54]"> · </span>
          <span className={(acc.skipped_today || 0) >= 5 ? "text-[#FF9040] font-bold" : "text-[#b9a06a]"}>
            {acc.skipped_today || 0} skipped{(acc.skipped_today || 0) >= 5 ? " ⚠" : ""}
          </span>
        </p>
        <p className="osrs-shadow text-[10px] mb-1.5" data-testid={`account-referral-${acc.profile_id}`}>
          <UserPlus size={10} className="inline mr-1 text-[#7CFC7C]" />
          {acc.referred_by_email ? (
            <span className="text-[#b9a06a]">
              Referred by <span className="text-[#FFCF3F]">{acc.referred_by_name || "?"}</span>
              <span className="text-[#8a7a54]"> ({acc.referred_by_email})</span>
            </span>
          ) : (
            <span className="text-[#8a7a54]">Joined directly</span>
          )}
          {(acc.referral_count || 0) > 0 && (
            <span className="text-[#7CFC7C]"> · invited {acc.referral_count}</span>
          )}
        </p>
        <div className="osrs-shadow text-[10px] text-[#8a7a54] uppercase mb-1">Current tasks ({acc.tasks.length})</div>
        {acc.tasks.length === 0 ? (
          <p className="osrs-shadow text-[#b9a06a] text-xs">No active tasks.</p>
        ) : (
          <ul className="space-y-1">
            {acc.tasks.slice(0, 6).map((t, ti) => {
              const Icon = CAT_ICON[t.category] || Star;
              return (
                <li key={ti} className="flex items-start gap-2" data-testid={`verified-task-${acc.profile_id}-${ti}`}>
                  <Icon size={13} className="text-[#7CFC7C] mt-0.5 shrink-0" />
                  <span className="osrs-shadow text-xs text-[#E6A519] min-w-0">
                    {t.title} <span className="text-[#7CFC7C]">+{t.points}</span>
                  </span>
                </li>
              );
            })}
            {acc.tasks.length > 6 && (
              <li className="osrs-shadow text-[10px] text-[#b9a06a] pl-5">+{acc.tasks.length - 6} more</li>
            )}
          </ul>
        )}
      </li>
    );
  };


  return (
    <>
      <button data-testid="admin-btn" onClick={openModal} className="osrs-btn px-3 py-2 text-sm flex items-center gap-2" title="Admin dashboard">
        <Shield size={16} className="text-[#FFCF3F]" /> Admin
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 px-4 py-8 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-5 w-full max-w-4xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="osrs-title text-2xl flex items-center gap-2"><Shield size={20} /> Admin dashboard</h3>
              <div className="flex items-center gap-3">
                {summary && (
                  <span className="inline-flex items-center gap-1.5 osrs-shadow text-[11px] text-[#7CFC7C]" data-testid="admin-live-indicator" title="Auto-refreshes every 15s">
                    <span className="w-2 h-2 rounded-full bg-[#4ade80] animate-pulse" style={{ boxShadow: "0 0 6px #4ade80" }} />
                    Live · updated {agoText}
                  </span>
                )}
                <button data-testid="admin-refresh" onClick={manualRefresh} disabled={refreshing} className="osrs-btn px-2 py-1" title="Refresh stats">
                  <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
                </button>
                <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-admin"><X size={16} /></button>
              </div>
            </div>

            {loading || !summary ? (
              <p className="osrs-shadow text-[#E6A519] py-16 text-center" data-testid="admin-loading">Loading realm stats...</p>
            ) : (
              <>
                <div className="mb-2 osrs-shadow text-[10px] text-[#8a7a54] uppercase tracking-wide">Conversion funnel (all-time)</div>
                <div className="osrs-panel p-3 mb-4" data-testid="admin-funnel">
                  {(() => {
                    const demo = summary.demo_all ?? 0;
                    const signups = summary.signups ?? 0;
                    const verified = summary.verified_profiles ?? 0;
                    const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);
                    const Stage = ({ icon, label, value, testid }) => (
                      <div className="flex-1 text-center" data-testid={testid}>
                        <div className="flex items-center justify-center mb-1">{icon}</div>
                        <div className="osrs-num osrs-title text-2xl">{(value || 0).toLocaleString()}</div>
                        <div className="osrs-shadow text-[10px] text-[#b9a06a] uppercase">{label}</div>
                      </div>
                    );
                    const Arrow = ({ rate }) => (
                      <div className="flex flex-col items-center px-1 shrink-0">
                        <ChevronRight size={18} className="text-[#8a7a54]" />
                        <span className="osrs-num text-[11px]" style={{ color: rate >= 25 ? "#7CFC7C" : rate >= 10 ? "#FFCF3F" : "#FF7A1A" }}>{rate}%</span>
                      </div>
                    );
                    return (
                      <div className="flex items-center justify-between gap-1">
                        <Stage icon={<ScrollText size={16} className="text-[#E6A519]" />} label="Demo boards" value={demo} testid="funnel-demo" />
                        <Arrow rate={pct(signups, demo)} />
                        <Stage icon={<UserPlus size={16} className="text-[#7CFC7C]" />} label="Signups" value={signups} testid="funnel-signups" />
                        <Arrow rate={pct(verified, signups)} />
                        <Stage icon={<CheckCircle2 size={16} className="text-[#4ade80]" />} label="Verified accts" value={verified} testid="funnel-verified" />
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-[#0f0d09]/50">
                    {stat(<TrendingUp size={16} className="text-[#7CFC7C]" />, "Demo boards today", summary.demo_today ?? 0, "admin-demo-today")}
                    {stat(<TrendingUp size={16} className="text-[#E6A519]" />, "Demo boards 7d", summary.demo_7d ?? 0, "admin-demo-7d")}
                  </div>
                </div>

                <div className="mb-2 osrs-shadow text-[10px] text-[#8a7a54] uppercase tracking-wide flex items-center justify-between">
                  <span>PWA installs (7d)</span>
                  <span className="text-[#7CFC7C] normal-case" data-testid="pwa-all-installed">
                    <CheckCircle2 size={11} className="inline -mt-0.5 mr-0.5" />{(summary.pwa_all_installed ?? 0).toLocaleString()} installed all-time
                  </span>
                </div>
                <div className="osrs-panel p-3 mb-4" data-testid="admin-pwa">
                  {(() => {
                    const pwa = summary.pwa_7d || { shown: 0, clicked: 0, installed: 0, dismissed: 0 };
                    const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);
                    const PStat = ({ icon, label, value, testid }) => (
                      <div className="flex-1 text-center" data-testid={testid}>
                        <div className="flex items-center justify-center mb-1">{icon}</div>
                        <div className="osrs-num osrs-title text-2xl">{(value || 0).toLocaleString()}</div>
                        <div className="osrs-shadow text-[10px] text-[#b9a06a] uppercase">{label}</div>
                      </div>
                    );
                    const Arrow = ({ rate }) => (
                      <div className="flex flex-col items-center px-1 shrink-0">
                        <ChevronRight size={18} className="text-[#8a7a54]" />
                        <span className="osrs-num text-[11px]" style={{ color: rate >= 25 ? "#7CFC7C" : rate >= 10 ? "#FFCF3F" : "#FF7A1A" }}>{rate}%</span>
                      </div>
                    );
                    return (
                      <div className="flex items-center justify-between gap-1">
                        <PStat icon={<Radio size={16} className="text-[#E6A519]" />} label="Prompt shown" value={pwa.shown} testid="pwa-shown" />
                        <Arrow rate={pct(pwa.clicked, pwa.shown)} />
                        <PStat icon={<Zap size={16} className="text-[#FFCF3F]" />} label="Install tapped" value={pwa.clicked} testid="pwa-clicked" />
                        <Arrow rate={pct(pwa.installed, pwa.clicked)} />
                        <PStat icon={<CheckCircle2 size={16} className="text-[#4ade80]" />} label="Installed" value={pwa.installed} testid="pwa-installed" />
                      </div>
                    );
                  })()}
                </div>

                <div className="mb-2 osrs-shadow text-[10px] text-[#8a7a54] uppercase tracking-wide">Engagement</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
                  {stat(<Radio size={16} className="text-[#4ade80]" />, "Online now", summary.online_now ?? 0, "admin-online-now")}
                  {stat(<Activity size={16} className="text-[#7CFC7C]" />, "Active 24h", summary.active_24h ?? 0, "admin-active-24h")}
                  {stat(<Activity size={16} className="text-[#E6A519]" />, "Active 7d", summary.active_7d ?? 0, "admin-active-7d")}
                  {stat(<Users size={16} className="text-[#E6A519]" />, "Signups", summary.signups ?? summary.users, "admin-stat-users")}
                  {stat(<Skull size={16} className="text-[#E6A519]" />, "Accounts", summary.profiles, "admin-stat-profiles", () => openAccounts(false))}
                  {stat(<CheckCircle2 size={16} className="text-[#7CFC7C]" />, "Verified", summary.verified_profiles, "admin-stat-verified", () => openAccounts(true))}
                  {stat(<UserPlus size={16} className="text-[#7CFC7C]" />, "Referred", summary.referred_users, "admin-stat-referred", openReferrals)}
                </div>

                <div className="mb-2 osrs-shadow text-[10px] text-[#8a7a54] uppercase tracking-wide">Growth</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4" data-testid="admin-signups">
                  {stat(<UserPlus size={16} className="text-[#7CFC7C]" />, "New today", summary.signups_today ?? 0, "admin-signups-today")}
                  {stat(<UserPlus size={16} className="text-[#E6A519]" />, "Last 7 days", summary.signups_7d ?? 0, "admin-signups-7d")}
                  {stat(<UserPlus size={16} className="text-[#b9a06a]" />, "Last 30 days", summary.signups_30d ?? 0, "admin-signups-30d")}
                  {stat(<Skull size={16} className="text-[#7CFC7C]" />, "Accts today", summary.new_accounts_today ?? 0, "admin-accts-today")}
                  {stat(<Skull size={16} className="text-[#E6A519]" />, "Accts 7d", summary.new_accounts_7d ?? 0, "admin-accts-7d")}
                  {stat(<Satellite size={16} className="text-[#4ade80]" />, "Synced", summary.synced_profiles ?? 0, "admin-synced")}
                </div>

                <div className="mb-2 osrs-shadow text-[10px] text-[#8a7a54] uppercase tracking-wide">Activity &amp; Bond</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
                  {stat(<ScrollText size={16} className="text-[#7CFC7C]" />, "Tasks done", summary.completed_tasks, "admin-stat-tasks")}
                  {stat(<Zap size={16} className="text-[#7CFC7C]" />, "Tasks today", summary.tasks_today ?? 0, "admin-tasks-today")}
                  {stat(<Zap size={16} className="text-[#E6A519]" />, "Tasks 7d", summary.tasks_7d ?? 0, "admin-tasks-7d")}
                  {stat(<Star size={16} className="text-[#FFCF3F]" />, "Total pts", (summary.total_points ?? 0).toLocaleString(), "admin-total-points")}
                  {stat(<Gift size={16} className="text-[#FF7A1A]" />, "Pending bonds", summary.pending_claims, "admin-stat-pending")}
                  {stat(<Coins size={16} className="text-[#FFCF3F]" />, `$${summary.season_pot}`, "Pot " + summary.season, "admin-stat-pot")}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                  <div>
                    <h4 className="osrs-title text-lg mb-2 flex items-center gap-2"><Filter size={16} /> Accounts by mode</h4>
                    <div className="border-2 border-black bg-black/30 overflow-x-auto" data-testid="admin-breakdown">
                      {!breakdown || breakdown.modes.length === 0 ? (
                        <p className="osrs-shadow text-[#b9a06a] text-sm p-4">No accounts yet.</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="osrs-bar text-[#E6A519] text-left text-[10px] uppercase">
                            <tr><th className="px-3 py-1.5">Mode</th><th className="px-2 py-1.5 text-right">Accts</th><th className="px-2 py-1.5 text-right">Verified</th><th className="px-2 py-1.5 text-right">Avg total</th><th className="px-3 py-1.5 text-right">Avg CB</th></tr>
                          </thead>
                          <tbody>
                            {breakdown.modes.map((m) => (
                              <tr key={m.gamemode} className="border-t border-[#0f0d09]/40" data-testid={`breakdown-${m.gamemode}`}>
                                <td className="px-3 py-1.5 osrs-num" style={{ color: modeColor(m.gamemode) }}>{modeLabel(m.gamemode)}</td>
                                <td className="px-2 py-1.5 text-right osrs-num text-[#FFCF3F]">{m.count}</td>
                                <td className="px-2 py-1.5 text-right osrs-shadow text-[#7CFC7C]">{m.verified}</td>
                                <td className="px-2 py-1.5 text-right osrs-num text-[#E6A519]">{m.avg_total.toLocaleString()}</td>
                                <td className="px-3 py-1.5 text-right osrs-num text-[#E6A519]">{m.avg_combat}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                    {breakdown?.top_total?.length > 0 && (
                      <div className="mt-2 osrs-shadow text-[11px] text-[#b9a06a]">
                        <Swords size={12} className="inline mr-1 text-[#FFCF3F]" />Highest total: {breakdown.top_total.map((t) => `${t.username} (${(t.total_level || 0).toLocaleString()})`).slice(0, 3).join(" · ")}
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="osrs-title text-lg mb-2 flex items-center gap-2"><UserPlus size={16} /> Recent signups</h4>
                    <div className="border-2 border-black bg-black/30 max-h-64 overflow-y-auto" data-testid="admin-recent-signups">
                      {recent.length === 0 ? (
                        <p className="osrs-shadow text-[#b9a06a] text-sm p-4">No signups yet.</p>
                      ) : (
                        <ul>
                          {recent.map((r) => (
                            <li key={r.user_id}>
                              <button
                                onClick={() => openUser(r.user_id)}
                                data-testid={`recent-signup-${r.user_id}`}
                                className="w-full flex items-center justify-between px-3 py-1.5 border-t border-[#0f0d09]/40 hover:bg-black/30 text-left"
                              >
                                <span className="min-w-0">
                                  <span className="osrs-shadow text-[#E6A519] block truncate">{r.name || r.email}</span>
                                  <span className="text-[10px] text-[#b9a06a]">
                                    {r.rsn ? <>tracking <span style={{ color: modeColor(r.gamemode) }}>{r.rsn}</span></> : "no account linked"} · {fmtWhen(r.created_at)}
                                  </span>
                                </span>
                                <ChevronRight size={14} className="text-[#8a7a54] shrink-0" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                  <div>
                    <h4 className="osrs-title text-lg mb-2 flex items-center gap-2"><Radio size={16} /> Signup sources</h4>
                    {(() => {
                      const w = breakdown?.sources_7d || [];
                      const totalW = w.reduce((a, s) => a + s.count, 0);
                      const top = w[0];
                      return (
                        <div className="osrs-panel p-3 mb-2 flex items-center justify-between gap-3" data-testid="admin-top-source-week">
                          <div className="min-w-0">
                            <div className="osrs-shadow text-[10px] text-[#8a7a54] uppercase tracking-wide flex items-center gap-1">
                              <TrendingUp size={12} className="text-[#7CFC7C]" /> Top channel this week
                            </div>
                            {top ? (
                              <>
                                <div className="osrs-title text-xl text-[#FFCF3F] truncate" data-testid="top-source-name">{top.source}</div>
                                <div className="osrs-shadow text-[11px] text-[#b9a06a]">
                                  <span className="text-[#7CFC7C] osrs-num">{top.count}</span> signup{top.count === 1 ? "" : "s"} in 7d
                                  {totalW > 0 && <> · <span className="text-[#E6A519]">{Math.round((top.count / totalW) * 100)}%</span> of attributed</>}
                                </div>
                              </>
                            ) : (
                              <div className="osrs-shadow text-[#b9a06a] text-sm mt-1" data-testid="top-source-empty">No attributed signups in the last 7 days yet.</div>
                            )}
                          </div>
                          <div className="osrs-num text-3xl text-[#7CFC7C] shrink-0" data-testid="top-source-count">{totalW}</div>
                        </div>
                      );
                    })()}
                    <div className="border-2 border-black bg-black/30" data-testid="admin-sources">
                      {!breakdown?.sources || breakdown.sources.length === 0 ? (
                        <p className="osrs-shadow text-[#b9a06a] text-sm p-4">No signups yet.</p>
                      ) : (() => {
                        const totalS = breakdown.sources.reduce((a, s) => a + s.count, 0) || 1;
                        return (
                          <ul>
                            {breakdown.sources.map((s) => (
                              <li key={s.source} className="px-3 py-1.5 border-t border-[#0f0d09]/40" data-testid={`source-${s.source}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="osrs-shadow text-[#E6A519] text-sm">{s.source}</span>
                                  <span className="osrs-num text-[#FFCF3F] text-sm">{s.count} <span className="text-[#8a7a54] text-[11px]">({Math.round((s.count / totalS) * 100)}%)</span></span>
                                </div>
                                <div className="h-1.5 bg-black border border-[#4b3a1e]"><div className="h-full bg-[#4caf3f]" style={{ width: `${Math.round((s.count / totalS) * 100)}%` }} /></div>
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                    </div>
                  </div>

                  <div>
                    <h4 className="osrs-title text-lg mb-2 flex items-center gap-2"><Zap size={16} /> Previewed, not claimed</h4>
                    <div className="border-2 border-black bg-black/30 max-h-64 overflow-y-auto" data-testid="admin-unclaimed">
                      {!previews || previews.unclaimed.length === 0 ? (
                        <p className="osrs-shadow text-[#b9a06a] text-sm p-4">No leaked previews — everyone who tried the demo signed up! 🎉</p>
                      ) : (
                        <>
                          <div className="osrs-bar px-3 py-1.5 text-[11px] osrs-shadow text-[#b9a06a]">
                            <span className="text-[#FF7A1A] osrs-num">{previews.total_unclaimed}</span> warm leads previewed a board but never claimed it · <span className="text-[#7CFC7C]">{previews.total_claimed}</span> claimed
                          </div>
                          <ul>
                            {previews.unclaimed.map((p) => (
                              <li key={p.rsn} className="flex items-center justify-between px-3 py-1.5 border-t border-[#0f0d09]/40" data-testid={`unclaimed-${p.rsn}`}>
                                <span className="osrs-num" style={{ color: modeColor(p.gamemode) }}>{p.rsn}</span>
                                <span className="osrs-shadow text-[11px] text-[#b9a06a]">{p.previews}× · {fmtWhen(p.last_previewed)}</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <h4 className="osrs-title text-lg mb-2 flex items-center gap-2"><Gift size={16} /> Bond claims &amp; winners</h4>
                <div className="border-2 border-black bg-black/30 mb-5 max-h-64 overflow-y-auto overflow-x-auto">
                  {winners.length === 0 ? (
                    <p className="osrs-shadow text-[#b9a06a] text-sm p-4">No season winners recorded yet.</p>
                  ) : (
                    <table className="w-full text-sm" data-testid="admin-winners-table">
                      <thead className="osrs-bar text-[#E6A519] text-left text-xs uppercase sticky top-0">
                        <tr>
                          <th className="px-3 py-2">Season</th>
                          <th className="px-3 py-2">Winner</th>
                          <th className="px-3 py-2">Claim RSN</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {winners.map((w) => {
                          const key = `${w.season}:${w.user_id}`;
                          const fulfilled = w.bond_status === "fulfilled";
                          return (
                            <tr key={key} className="border-t border-[#0f0d09]/50" data-testid={`admin-winner-${w.season}`}>
                              <td className="px-3 py-2 osrs-num text-[#FFCF3F]">{w.season}</td>
                              <td className="px-3 py-2">
                                <div className="osrs-shadow text-[#E6A519]">{w.name}</div>
                                <div className="text-[10px] text-[#b9a06a]">{w.email} · {w.total_points} pts</div>
                              </td>
                              <td className="px-3 py-2 osrs-shadow text-[#E6A519]">
                                {w.claim?.rsn ? (
                                  <>
                                    <div className="text-[#FFCF3F]">{w.claim.rsn}</div>
                                    {w.claim.note && <div className="text-[10px] text-[#b9a06a] max-w-[180px] truncate" title={w.claim.note}>“{w.claim.note}”</div>}
                                  </>
                                ) : <span className="text-[#b9a06a] text-xs">— not claimed —</span>}
                              </td>
                              <td className="px-3 py-2">
                                {fulfilled ? (
                                  <span className="inline-flex items-center gap-1 text-[#7CFC7C] text-xs"><CheckCircle2 size={13} /> Fulfilled</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[#FF7A1A] text-xs"><Clock size={13} /> Pending</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {!fulfilled && w.claim?.rsn && (
                                  <button
                                    data-testid={`fulfill-${w.season}`}
                                    onClick={() => fulfill(w)}
                                    disabled={fulfilling === key}
                                    className="osrs-btn osrs-btn-green px-3 py-1.5 text-xs"
                                  >
                                    {fulfilling === key ? "..." : "Mark sent"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <h4 className="osrs-title text-lg mb-2 flex items-center gap-2"><Trophy size={16} /> This season's top 10 ({summary.season})</h4>
                <div className="border-2 border-black bg-black/30 max-h-56 overflow-y-auto">
                  {standings.length === 0 ? (
                    <p className="osrs-shadow text-[#b9a06a] text-sm p-4">No points scored this season yet.</p>
                  ) : (
                    <ul data-testid="admin-standings">
                      {standings.map((e) => (
                        <li key={e.rank} className="flex items-center justify-between px-3 py-1.5 border-t border-[#0f0d09]/40">
                          <span className="osrs-shadow text-[#E6A519]">
                            <span className="osrs-num text-[#FFCF3F] mr-2">#{e.rank}</span>{e.name}
                            {e.gamemode && e.gamemode !== "main" && (
                              <span className="osrs-num text-[10px] ml-1 uppercase" style={{ color: modeColor(e.gamemode) }}>
                                {e.gamemode === "hardcore" ? "HCIM" : e.gamemode === "ultimate" ? "UIM" : "IM"}
                              </span>
                            )}
                          </span>
                          <span className="osrs-num text-[#7CFC7C]">{e.total_points} pts</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {userDetail && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => setUserDetail(null)} data-testid="admin-user-modal">
          <div className="osrs-panel p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            {userDetail.loading ? (
              <p className="osrs-shadow text-[#E6A519] py-8 text-center">Loading user...</p>
            ) : (
              <>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="osrs-title text-xl" data-testid="user-detail-name">{userDetail.name || userDetail.email}</h3>
                    <p className="osrs-shadow text-[11px] text-[#b9a06a]">{userDetail.email} · {userDetail.role} · joined {fmtWhen(userDetail.created_at)}</p>
                    {userDetail.banned && (
                      <p className="osrs-shadow text-[11px] text-[#FF6666] mt-0.5" data-testid="user-banned-note">
                        ⛔ Banned{userDetail.banned_reason ? ` · ${userDetail.banned_reason}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {userDetail.role !== "admin" && (
                      <button
                        data-testid="user-ban-btn"
                        onClick={() => banUser(userDetail.user_id, userDetail.email, userDetail.banned)}
                        className={`osrs-btn px-3 py-1 text-xs ${userDetail.banned ? "" : "osrs-btn-red"}`}
                      >
                        {userDetail.banned ? "Unban" : "Ban account"}
                      </button>
                    )}
                    <button onClick={() => setUserDetail(null)} className="osrs-btn px-2 py-1" data-testid="close-user-detail"><X size={16} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {stat(<CheckCircle2 size={16} className="text-[#4ade80]" />, "Done", userDetail.completed_tasks, "user-done")}
                  {stat(<ScrollText size={16} className="text-[#E6A519]" />, "Active", userDetail.active_tasks, "user-active")}
                  {stat(<Star size={16} className="text-[#FFCF3F]" />, "Points", userDetail.total_points, "user-points")}
                </div>
                <p className="osrs-shadow text-[11px] text-[#8a7a54] uppercase mb-1">Accounts · last seen {fmtWhen(userDetail.last_seen)}</p>
                {userDetail.accounts.length === 0 ? (
                  <p className="osrs-shadow text-[#b9a06a] text-sm">No RuneScape account linked.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {userDetail.accounts.map((acc, i) => (
                      <li key={i} className="flex items-center justify-between bg-black/30 border border-[#0f0d09] px-3 py-1.5" data-testid={`user-account-${i}`}>
                        <span className="osrs-num" style={{ color: modeColor(acc.gamemode) }}>{acc.rsn} <span className="text-[10px] uppercase">{modeLabel(acc.gamemode)}</span></span>
                        <span className="osrs-shadow text-[11px] text-[#b9a06a]">CB {acc.combat_level} · Total {(acc.total_level || 0).toLocaleString()} · {acc.streak}🔥</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {accountsModal && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 p-4 py-8 overflow-y-auto" onClick={() => setAccountsModal(null)} data-testid="admin-verified-modal">
          <div className="osrs-panel p-5 w-full max-w-3xl my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="osrs-title text-xl flex items-center gap-2" data-testid="verified-modal-title">
                {accountsModal.verifiedOnly ? <CheckCircle2 size={18} className="text-[#7CFC7C]" /> : <Skull size={18} className="text-[#E6A519]" />}
                {accountsModal.title}
                {!accountsModal.loading && <span className="osrs-num text-[#FFCF3F] text-base">({accountsModal.count})</span>}
              </h3>
              <button onClick={() => setAccountsModal(null)} className="osrs-btn px-2 py-1" data-testid="close-verified"><X size={16} /></button>
            </div>
            {accountsModal.loading ? (
              <p className="osrs-shadow text-[#E6A519] py-8 text-center">Loading accounts...</p>
            ) : accountsModal.accounts.length === 0 ? (
              <p className="osrs-shadow text-[#b9a06a] text-sm py-6 text-center">No accounts yet.</p>
            ) : (() => {
              // Detect the same OSRS name claimed under 2+ distinct emails (multi-accounting to game the system).
              const emailsByRsn = {};
              accountsModal.accounts.forEach((a) => {
                const key = (a.rsn || "").toLowerCase();
                (emailsByRsn[key] = emailsByRsn[key] || new Set()).add((a.owner_email || "").toLowerCase());
              });
              const isDup = (a) => (emailsByRsn[(a.rsn || "").toLowerCase()] || new Set()).size > 1;
              const dupCount = accountsModal.accounts.filter(isDup).length;
              const freshCount = accountsModal.accounts.filter((a) => a.suspicious_fresh).length;
              const q = acctFilter.trim().toLowerCase();
              const list = accountsModal.accounts.filter((a) => {
                if (dupOnly && !isDup(a)) return false;
                if (freshOnly && !a.suspicious_fresh) return false;
                if (!q) return true;
                return (a.owner_email || "").toLowerCase().includes(q) || (a.rsn || "").toLowerCase().includes(q);
              });
              return (
                <>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <input
                      data-testid="accounts-filter"
                      value={acctFilter}
                      onChange={(e) => setAcctFilter(e.target.value)}
                      placeholder="Filter by email or RSN…"
                      className="osrs-input flex-1 min-w-[140px] px-3 py-1.5 text-sm"
                    />
                    <button
                      data-testid="accounts-dup-toggle"
                      onClick={() => { setDupOnly((v) => !v); setFreshOnly(false); }}
                      className={`osrs-btn px-3 py-1.5 text-xs whitespace-nowrap ${dupOnly ? "osrs-btn-green" : ""}`}
                      title="Show only OSRS names claimed under multiple emails"
                    >
                      ⚠ Dupes{dupCount ? ` (${dupCount})` : ""}
                    </button>
                    <button
                      data-testid="accounts-fresh-toggle"
                      onClick={() => { setFreshOnly((v) => !v); setDupOnly(false); }}
                      className={`osrs-btn px-3 py-1.5 text-xs whitespace-nowrap ${freshOnly ? "osrs-btn-green" : ""}`}
                      title="Low-level accounts already earning points — possible fresh-account point farming"
                    >
                      🌱 Fresh farm{freshCount ? ` (${freshCount})` : ""}
                    </button>
                  </div>
                  {dupCount > 0 && !dupOnly && (
                    <p className="osrs-shadow text-[#FF9040] text-[11px] mb-1" data-testid="accounts-dup-warning">
                      ⚠ {dupCount} account{dupCount > 1 ? "s" : ""} share an OSRS name across different emails — possible multi-accounting.
                    </p>
                  )}
                  {freshCount > 0 && !freshOnly && (
                    <p className="osrs-shadow text-[#FF9040] text-[11px] mb-1" data-testid="accounts-fresh-warning">
                      🌱 {freshCount} low-level account{freshCount > 1 ? "s" : ""} (&lt;750 total) already banking points — possible fresh-account farming.
                    </p>
                  )}
                  <p className="osrs-shadow text-[10px] text-[#8a7a54] mb-2" data-testid="accounts-filter-count">Showing {list.length} of {accountsModal.accounts.length} account{accountsModal.accounts.length !== 1 ? "s" : ""}</p>
                  {list.length === 0 ? (
                    <p className="osrs-shadow text-[#b9a06a] text-sm py-6 text-center">No accounts match.</p>
                  ) : (() => {
                    // Group accounts under their owning email/site-account.
                    const groups = {};
                    list.forEach((a) => {
                      const key = a.user_id || a.owner_email || "unknown";
                      if (!groups[key]) groups[key] = { key, email: a.owner_email || "—", user_id: a.user_id, banned: a.banned, accounts: [] };
                      groups[key].accounts.push(a);
                    });
                    const groupArr = Object.values(groups).sort((a, b) => b.accounts.length - a.accounts.length);
                    const pageCount = Math.max(1, Math.ceil(groupArr.length / PAGE_SIZE));
                    const safePage = Math.min(page, pageCount - 1);
                    const shown = groupArr.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
                    return (
                      <>
                      <ul className="space-y-2" data-testid="accounts-email-list">
                        {shown.map((g) => {
                          const isOpen = expandedEmails.has(g.key) || !!q || dupOnly || freshOnly;
                          const anyDup = g.accounts.some(isDup);
                          const anyFresh = g.accounts.some((a) => a.suspicious_fresh);
                          const totalPts = g.accounts.reduce((s, a) => s + (a.points_earned || 0), 0);
                          const toggle = () => setExpandedEmails((prev) => {
                            const next = new Set(prev);
                            next.has(g.key) ? next.delete(g.key) : next.add(g.key);
                            return next;
                          });
                          return (
                            <li key={g.key} className={`border ${(anyDup || anyFresh || g.banned) ? "border-[#FF9040]" : "border-[#0f0d09]"}`} data-testid={`email-group-${g.key}`}>
                              <div className="flex items-center justify-between gap-2 bg-black/40 px-3 py-2">
                                <button onClick={toggle} className="flex items-center gap-2 min-w-0 text-left flex-1" data-testid={`email-toggle-${g.key}`}>
                                  <ChevronRight size={14} className={`text-[#E6A519] shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                                  <Users size={13} className="text-[#8a7a54] shrink-0" />
                                  <span className="osrs-num text-[#FFCF3F] text-sm truncate">{g.email}</span>
                                  <span className="osrs-shadow text-[10px] text-[#b9a06a] shrink-0">· {g.accounts.length} acct{g.accounts.length > 1 ? "s" : ""}</span>
                                  {anyDup && <span className="text-[10px] text-[#FF9040] shrink-0">⚠</span>}
                                  {anyFresh && <span className="text-[10px] text-[#FF9040] shrink-0">🌱</span>}
                                  {g.banned && <span className="text-[10px] text-[#FF6666] shrink-0">⛔ BANNED</span>}
                                </button>
                                <span className="osrs-shadow text-[10px] text-[#7CFC7C] shrink-0 hidden sm:inline">{totalPts.toLocaleString()} pts</span>
                                {g.user_id && (
                                  <button
                                    data-testid={`ban-email-${g.key}`}
                                    onClick={() => banUser(g.user_id, g.email, g.banned)}
                                    className={`osrs-btn px-2 py-0.5 text-[10px] shrink-0 ${g.banned ? "" : "osrs-btn-red"}`}
                                    title={g.banned ? "Unban this email" : "Ban this email (all accounts below)"}
                                  >
                                    {g.banned ? "Unban" : "Ban"}
                                  </button>
                                )}
                              </div>
                              {isOpen && (
                                <ul className="space-y-2 p-2">
                                  {g.accounts.map((acc) => renderAccountCard(acc, isDup(acc)))}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      {pageCount > 1 && (
                        <div className="flex items-center justify-between gap-2 mt-3" data-testid="accounts-pagination">
                          <button
                            data-testid="accounts-prev-page"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            className="osrs-btn px-3 py-1.5 text-xs disabled:opacity-40"
                          >
                            ‹ Prev
                          </button>
                          <span className="osrs-shadow text-[11px] text-[#b9a06a]" data-testid="accounts-page-indicator">
                            Page <span className="osrs-num text-[#FFCF3F]">{safePage + 1}</span> of <span className="osrs-num text-[#FFCF3F]">{pageCount}</span>
                          </span>
                          <button
                            data-testid="accounts-next-page"
                            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                            disabled={safePage >= pageCount - 1}
                            className="osrs-btn px-3 py-1.5 text-xs disabled:opacity-40"
                          >
                            Next ›
                          </button>
                        </div>
                      )}
                      </>
                    );
                  })()}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {referralsModal && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 p-4 py-8 overflow-y-auto" onClick={() => setReferralsModal(null)} data-testid="admin-referrals-modal">
          <div className="osrs-panel p-5 w-full max-w-2xl my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="osrs-title text-xl flex items-center gap-2">
                <UserPlus size={18} className="text-[#7CFC7C]" /> Referred users
                {!referralsModal.loading && <span className="osrs-num text-[#FFCF3F] text-base">({referralsModal.total_referred})</span>}
              </h3>
              <button onClick={() => setReferralsModal(null)} className="osrs-btn px-2 py-1" data-testid="close-referrals"><X size={16} /></button>
            </div>

            {referralsModal.loading ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-8" data-testid="referrals-loading">Loading referrals...</p>
            ) : (referralsModal.referrers || []).length === 0 ? (
              <p className="osrs-shadow text-[#b9a06a] text-center py-8" data-testid="referrals-empty">
                No one has joined via an invite link yet.
              </p>
            ) : (
              <>
                <p className="osrs-shadow text-[10px] text-[#8a7a54] mb-2" data-testid="referrals-summary">
                  <span className="osrs-num text-[#7CFC7C]">{referralsModal.total_referred}</span> users referred by{" "}
                  <span className="osrs-num text-[#FFCF3F]">{referralsModal.total_referrers}</span> referrer{referralsModal.total_referrers !== 1 ? "s" : ""}
                  {(referralsModal.total_flagged || 0) > 0 && (
                    <span className="text-[#FF9040]"> · <span className="osrs-num">{referralsModal.total_flagged}</span> IP-flagged ⚠</span>
                  )}
                </p>
                <ul className="space-y-2" data-testid="referrals-list">
                  {referralsModal.referrers.map((g) => (
                    <li key={g.referrer_id} className={`border ${(g.flagged_count || 0) > 0 ? "border-[#FF9040]" : "border-[#0f0d09]"}`} data-testid={`referrer-group-${g.referrer_id}`}>
                      <div className="flex items-center justify-between gap-2 bg-black/40 px-3 py-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <UserPlus size={13} className="text-[#7CFC7C] shrink-0" />
                          <span className="osrs-num text-[#FFCF3F] text-sm truncate">{g.referrer_name}</span>
                          <span className="osrs-shadow text-[10px] text-[#8a7a54] truncate hidden sm:inline">{g.referrer_email}</span>
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          {(g.flagged_count || 0) > 0 && (
                            <span className="osrs-shadow text-[10px] text-[#FF9040]" title="Invitees with a matching signup IP">⚠ {g.flagged_count} flagged</span>
                          )}
                          <span className="osrs-shadow text-[10px] text-[#7CFC7C]">invited {g.invitees.length}</span>
                        </span>
                      </div>
                      <ul className="divide-y divide-[#0f0d09]/60">
                        {g.invitees.map((inv) => (
                          <li key={inv.user_id} className={`flex items-center justify-between gap-2 px-3 py-1.5 ${inv.ip_flag ? "bg-[#3a2213]" : ""}`} data-testid={`invitee-${inv.user_id}`}>
                            <button onClick={() => { setReferralsModal(null); openUser(inv.user_id); }} className="flex items-center gap-2 min-w-0 text-left hover:brightness-125 transition-[filter]">
                              <span className="osrs-shadow text-[#E6A519] text-sm truncate">{inv.name}</span>
                              <span className="osrs-shadow text-[10px] text-[#8a7a54] truncate hidden sm:inline">{inv.email}</span>
                              {inv.ip_flag && <span className="text-[10px] text-[#FF9040] shrink-0" title={inv.ip_flag_reason}>⚠ IP</span>}
                              {inv.banned && <span className="text-[10px] text-[#FF6666] shrink-0">⛔</span>}
                            </button>
                            <span className="flex items-center gap-2 shrink-0">
                              {inv.reward_status === "held" && (
                                <>
                                  <span className="osrs-shadow text-[10px] text-[#FF9040]" title="+1 skip reward withheld pending review">reward held</span>
                                  <button data-testid={`approve-referral-${inv.user_id}`} onClick={() => resolveReferral(inv.user_id, "approve")} className="osrs-btn osrs-btn-green px-2 py-0.5 text-[10px]" title="Grant +1 skip to both">Approve</button>
                                  <button data-testid={`void-referral-${inv.user_id}`} onClick={() => resolveReferral(inv.user_id, "void")} className="osrs-btn osrs-btn-red px-2 py-0.5 text-[10px]" title="Void the reward">Void</button>
                                </>
                              )}
                              {inv.reward_status === "voided" && <span className="osrs-shadow text-[10px] text-[#8a7a54]">voided</span>}
                              {inv.reward_status === "granted" && <span className="osrs-shadow text-[10px] text-[#7CFC7C]" title="+1 skip granted to both">rewarded ✓</span>}
                              <span className="osrs-shadow text-[10px] text-[#8a7a54]">{fmtWhen(inv.created_at)}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
